/**
 * Created by calhosh on 7/13/2017.
 */

import PropTypes from 'prop-types';
import React from 'react';
import ReactSelect from '@ey/canvascoreservices/ReactSelect';
import {useIsAresView} from '../../../util/Ares/customHooks';
import Utility from '../../../util/utilityfunctions';

SelectDropdown.propTypes = {
	/** Accessibility - Aira label Value */
	airaLabelValue: PropTypes.string,
	/** Remove focus from the input when the user selects an option (handy for dismissing the keyboard on touch devices) */
	blurInputOnSelect: PropTypes.bool,
	/** Custom class name the user is assigning. */
	className: PropTypes.string,
	/** Localized value to display if clear all icon is showing. */
	clearAllValuesLabel: PropTypes.string,
	/** If true, close the select menu when the user scrolls the document/body.
		 If a function, takes a standard javascript ScrollEvent you return a boolean:
		 true => The menu closes
		 false => The menu stays open
		 This is useful when you have a scrollable modal and want to portal the menu out, but want to avoid graphical issues.*/
	closeMenuOnScroll: PropTypes.bool,
	/** Close the select menu when the user selects an option. */
	closeMenuOnSelect: PropTypes.bool,
	/** If set to true this will not display the selected value. Defaults to true. */
	controlShouldRenderValue: PropTypes.bool,
	/** Indicates whether to use the default clear icon on custom canvas clear icon. */
	customClearIcon: PropTypes.bool,
	/** Whether the select is expanded initially. */
	defaultMenuIsOpen: PropTypes.bool,
	/** The default selected value of shape: [{value: value, label: label}]*/
	defaultValue: PropTypes.shape({value: PropTypes.number, label: PropTypes.string}),
	/** Error message. */
	errorMessage: PropTypes.string,
	/** Custom method to filter whether an option should be displayed in the menu. **/
	filterOption: PropTypes.func,
	/** Formats group labels in the menu and control as React components. A custom renderer. */
	formatGroupLabel: PropTypes.func,
	/** Formats option labels in the menu and control as React components. A custom renderer. */
	formatOptionLabel: PropTypes.func,
	/** String label to place outside of the select. */
	formLabel: PropTypes.string,
	/** Group Heading can be any component. */
	groupHeading: PropTypes.func,
	/** The id to set on the SelectContainer component. */
	id: PropTypes.string,
	/** Is this select making an async server call based on the input? If so set isAsync === true.**/
	isAsync: PropTypes.bool,
	/** Is the select value clearable. To go back to a null state use isClearable = true. */
	isClearable: PropTypes.bool,
	/** Is the select disabled. */
	isDisabled: PropTypes.bool,
	/** When isAsync is true, is loading data. */
	isLoading: PropTypes.bool,
	/** Support multiple selected options. */
	isMulti: PropTypes.bool,
	/** Whether to enable search functionality. */
	isSearchable: PropTypes.bool,
	/** Whether to keep showing form label when lost focus. Defaults to false. */
	keepFormLabelOnBlur: PropTypes.bool,
	/** When isAsync is true the options need to be set using loadOptions instead of options. **/
	loadOptions: PropTypes.arrayOf(PropTypes.shape({value: PropTypes.number, label: PropTypes.string})),
	/** String defining the name of the control. */
	name: PropTypes.string,
	/** function to call when no options are available. If not provided 'No results found' will be shown. */
	noOptionsMessage: PropTypes.func,
	/** Whether to hide the menu on blur. */
	onBlurHideMenu: PropTypes.bool,
	/** Handle change events on the select. */
	onChange: PropTypes.func,
	/** Handle on focus event on the select. */
	onFocus: PropTypes.func,
	/** Set focus on select when label clicked. */
	onLabelClickSetFocus: PropTypes.bool,
	/** Handle key down event. */
	onInputKeyDown: PropTypes.func,
	/** Indicator showing whether to open menu on focus. */
	openMenuOnFocus: PropTypes.bool,
	/** Array of options that populate the select menu. */
	options: PropTypes.arrayOf(PropTypes.shape({value: PropTypes.number, label: PropTypes.string})),
	/** Placeholder for the select value. Different from form name. Defaulted to null to not show. Accepts a node so callers can ellipse it. */
	placeholder: PropTypes.node,
	/** Indicates whether you want to search in the label and value field or just label. True means search in label & value. False means search label only. */
	searchInLabelOrValue: PropTypes.bool,
	/** Value indicating whether to show dropdown arrow when select is disabled. */
	showDropArrowOnDisabled: PropTypes.bool,
	/** Value indicating whether to show the error message if applicable. */
	showErrorMessage: PropTypes.bool,
	/** The value of the search input. An array of shape: [{value: value, label: label}] */
	value: PropTypes.arrayOf(PropTypes.shape({value: PropTypes.number, label: PropTypes.string})),
	/** Show sticky element in menu list footer. */
	footer: PropTypes.element,
	/** Placement of the menu in relation to the control.
		  'auto' will flip when there isn't enough space below the control.
		  'bottom' bottom placement. 'top' top placement. */
	menuPlacement: PropTypes.string,
	/** When an option is selected hide it from the options. Defaults to false.*/
	hideSelectedOptions: PropTypes.bool,
	/** Theme value dark or light*/
	theme: PropTypes.string,
	/** Disable option*/
	isOptionDisabled: PropTypes.bool,
	/**Custom clear icon component */
	customClearIconComponent: PropTypes.node,
	/** Max options before scroll kicks in*/
	maxOptionsBeforeScroll: PropTypes.number,
	/** Enables menu portaling, in case that menu options need to overflow its container. */
	isMenuPortalingEnabled: PropTypes.bool
};

// Default props are now handled via default parameter values in the function signature.

export default function SelectDropdown(props) {
	const {
		airaLabelValue = 'select',
		blurInputOnSelect = false,
		closeMenuOnScroll = true,
		closeMenuOnSelect = true,
		controlShouldRenderValue = true,
		customClearIcon = false,
		defaultMenuIsOpen = false,
		isAsync = false,
		isClearable = false,
		isDisabled = false,
		isLoading = false,
		isMulti = false,
		isSearchable = true,
		keepFormLabelOnBlur = false,
		name = 'react-select',
		onBlurHideMenu = false,
		onLabelClickSetFocus = true,
		openMenuOnFocus = true,
		placeholder = null,
		searchInLabelOrValue = false,
		showDropArrowOnDisabled = true,
		showErrorMessage = false,
		hideSelectedOptions = false,
		isOptionDisabled = false,
		maxOptionsBeforeScroll = 6,
		isMenuPortalingEnabled = false,
		...rest
	} = props;

	const isAresView = props.isAresErrorIcon !== undefined ? props.isAresErrorIcon : (useIsAresView() ?? false);

	return (
		<ReactSelect
			isAresView={isAresView}
			theme={Utility.getTheme()}
			airaLabelValue={airaLabelValue}
			blurInputOnSelect={blurInputOnSelect}
			closeMenuOnScroll={closeMenuOnScroll}
			closeMenuOnSelect={closeMenuOnSelect}
			controlShouldRenderValue={controlShouldRenderValue}
			customClearIcon={customClearIcon}
			defaultMenuIsOpen={defaultMenuIsOpen}
			isAsync={isAsync}
			isClearable={isClearable}
			isDisabled={isDisabled}
			isLoading={isLoading}
			isMulti={isMulti}
			isSearchable={isSearchable}
			keepFormLabelOnBlur={keepFormLabelOnBlur}
			name={name}
			onBlurHideMenu={onBlurHideMenu}
			onLabelClickSetFocus={onLabelClickSetFocus}
			openMenuOnFocus={openMenuOnFocus}
			placeholder={placeholder}
			searchInLabelOrValue={searchInLabelOrValue}
			showDropArrowOnDisabled={showDropArrowOnDisabled}
			showErrorMessage={showErrorMessage}
			hideSelectedOptions={hideSelectedOptions}
			isOptionDisabled={isOptionDisabled}
			maxOptionsBeforeScroll={maxOptionsBeforeScroll}
			isMenuPortalingEnabled={isMenuPortalingEnabled}
			{...rest}
		/>
	);
}
